import React from 'react';
import { ChevronLeft, ChevronRight, X, Play } from 'lucide-react';

export const ImageLightbox = ({ items, currentIndex, onClose, onPrev, onNext }) => {
  const item = items[currentIndex];
  if (!item) return null;

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft') onPrev();
    if (e.key === 'ArrowRight') onNext();
  };

  return (
    <div
      className="fixed inset-0 bg-[#0A0A0B]/97 z-50 flex items-center justify-center"
      onClick={onClose}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      data-testid="portfolio-lightbox"
      role="dialog"
      aria-label="Image viewer"
    >
      <button onClick={onClose} className="absolute top-6 right-6 text-[#E8E4DF]/50 hover:text-[#F0EDE8] transition-colors z-10" aria-label="Close">
        <X className="w-6 h-6" />
      </button>

      {items.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center text-[#E8E4DF]/40 hover:text-[#14B8A6] transition-colors z-10"
            aria-label="Previous image"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center text-[#E8E4DF]/40 hover:text-[#14B8A6] transition-colors z-10"
            aria-label="Next image"
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
        </>
      )}

      <div className="max-w-5xl max-h-[90vh] flex flex-col items-center px-16" onClick={(e) => e.stopPropagation()}>
        <img src={item.image} alt={item.title} className="max-w-full max-h-[75vh] object-contain" />
        <div className="mt-4 text-center">
          <h3 className="text-lg font-medium text-[#F0EDE8]">{item.title}</h3>
          {item.description && <p className="text-[#E8E4DF]/60 text-sm mt-1">{item.description}</p>}
          <p className="text-[#E8E4DF]/25 text-xs mt-2" data-font="label">{currentIndex + 1} / {items.length}</p>
        </div>
      </div>
    </div>
  );
};

export const VideoModal = ({ videos, currentIndex, onClose, onPrev, onNext }) => {
  const video = videos[currentIndex];
  if (!video) return null;

  return (
    <div
      className="fixed inset-0 bg-[#0A0A0B]/97 z-50 flex items-center justify-center"
      onClick={onClose}
      data-testid="video-modal"
      role="dialog"
      aria-label="Video player"
    >
      <button onClick={onClose} className="absolute top-6 right-6 text-[#E8E4DF]/50 hover:text-[#F0EDE8] transition-colors z-10" aria-label="Close">
        <X className="w-6 h-6" />
      </button>

      {videos.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center text-[#E8E4DF]/40 hover:text-[#14B8A6] transition-colors z-10"
            aria-label="Previous video"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center text-[#E8E4DF]/40 hover:text-[#14B8A6] transition-colors z-10"
            aria-label="Next video"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
        </>
      )}

      <div className="w-full max-w-4xl px-16" onClick={(e) => e.stopPropagation()}>
        <div className="aspect-video">
          <iframe
            key={video.videoId}
            width="100%" height="100%"
            src={`https://www.youtube.com/embed/${video.videoId}?autoplay=1`}
            title={video.title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
        <div className="mt-4 text-center">
          <h3 className="text-lg font-medium text-[#F0EDE8]">{video.title}</h3>
          {video.description && <p className="text-[#E8E4DF]/60 text-sm mt-1">{video.description}</p>}
          <p className="text-[#E8E4DF]/25 text-xs mt-2" data-font="label">{currentIndex + 1} / {videos.length}</p>
        </div>
      </div>
    </div>
  );
};

export const VideoThumb = ({ video, onClick }) => (
  <div
    className="group relative cursor-pointer corner-frame"
    onClick={onClick}
    data-testid={`video-thumb-${video.id}`}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
  >
    <div className="aspect-video overflow-hidden">
      <img
        src={video.thumbnail}
        alt={`${video.title} - cinematic video trailer`}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
      />
      <div className="absolute inset-0 bg-[#0A0A0B]/30 group-hover:bg-[#0A0A0B]/50 transition-colors duration-300 flex items-center justify-center">
        <div className="w-14 h-14 border border-[#F0EDE8]/30 rounded-full flex items-center justify-center group-hover:border-[#14B8A6]/60 group-hover:bg-[#14B8A6]/10 transition-all duration-300">
          <Play className="w-6 h-6 text-[#F0EDE8]/80 ml-0.5" />
        </div>
      </div>
    </div>
    <div className="pt-3">
      <h3 className="text-base font-medium text-[#F0EDE8] group-hover:text-[#14B8A6] transition-colors">{video.title}</h3>
      {video.description && <p className="text-[#E8E4DF]/50 text-sm mt-0.5">{video.description}</p>}
    </div>
  </div>
);
